/* RoyalMouthguard lightweight on-demand WebGL model viewer.
   One canvas, two meshes, no third-party runtime or render loop while idle. */
(() => {
'use strict';
const VERT = `attribute vec3 aPosition; attribute vec3 aNormal; attribute vec2 aUV;
uniform mat4 uVP;
varying vec3 vNormal; varying vec3 vPosition; varying vec2 vUV;
void main(){vPosition=aPosition;vNormal=aNormal;vUV=aUV;gl_Position=uVP*vec4(aPosition,1.0);}`;
const FRAG = `precision mediump float;
varying vec3 vNormal; varying vec3 vPosition; varying vec2 vUV;
uniform sampler2D uDesign;uniform bool uGold;uniform vec3 uCamera;
void main(){
  vec3 n=normalize(vNormal);vec3 eye=normalize(uCamera-vPosition);
  if(dot(n,eye)<0.0)n=-n;

  vec3 light1=normalize(vec3(-0.45,0.95,-0.85));
  vec3 light2=normalize(vec3(0.95,0.35,0.75));
  vec3 rimDir=normalize(vec3(0.0,0.25,1.0));

  float ndl1=max(dot(n,light1),0.0);
  float ndl2=max(dot(n,light2),0.0);
  float diffuse=ndl1*0.34+ndl2*0.20;

  float hemi=mix(0.16,0.34,clamp(n.y*0.5+0.5,0.0,1.0));
  float fresnel=pow(1.0-max(dot(n,eye),0.0),2.2);
  float rim=max(dot(n,rimDir),0.0)*fresnel;

  float spec=pow(max(dot(reflect(-light1,n),eye),0.0),34.0);
  float shine=pow(max(dot(reflect(-light2,n),eye),0.0),52.0);

  if(uGold){
    vec3 gold=vec3(146.0,119.0,72.0)/255.0;
    vec3 c=gold*(0.62+hemi+diffuse*0.54)
      + vec3(1.0,0.84,0.56)*spec*0.46
      + vec3(0.95,0.86,0.70)*shine*0.24
      + vec3(0.68,0.58,0.36)*rim*0.35;
    gl_FragColor=vec4(min(c,vec3(1.0)),1.0);
  } else {
    vec3 c=texture2D(uDesign,clamp(vUV,vec2(0.0),vec2(1.0))).rgb;
    float luma=dot(c,vec3(0.2126,0.7152,0.0722));
    float darkBoost=1.0-smoothstep(0.10,0.38,luma);
    vec3 lit = c*(0.22+hemi+diffuse*0.72)
      + vec3(1.0)*spec*(0.15+darkBoost*0.10)
      + vec3(0.78,0.84,0.92)*shine*(0.10+darkBoost*0.08)
      + vec3(0.60,0.70,0.82)*rim*(0.20+darkBoost*0.22);
    gl_FragColor=vec4(min(lit,vec3(1.0)),1.0);
  }
}`;
function shader(gl,kind,source){const s=gl.createShader(kind);gl.shaderSource(s,source);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(s));return s;}
function program(gl){const p=gl.createProgram();const v=shader(gl,gl.VERTEX_SHADER,VERT),f=shader(gl,gl.FRAGMENT_SHADER,FRAG);gl.attachShader(p,v);gl.attachShader(p,f);gl.linkProgram(p);gl.deleteShader(v);gl.deleteShader(f);if(!gl.getProgramParameter(p,gl.LINK_STATUS))throw new Error(gl.getProgramInfoLog(p));return p;}
function cross(a,b){return[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];}
function sub(a,b){return a.map((x,i)=>x-b[i]);}
function normalize(a){const l=Math.hypot(...a)||1;return a.map(v=>v/l);}
function view(eye,target){const z=normalize(sub(eye,target)),x=normalize(cross([0,1,0],z)),y=cross(z,x);return[
 x[0],y[0],z[0],0, x[1],y[1],z[1],0, x[2],y[2],z[2],0,
 -x.reduce((s,v,i)=>s+v*eye[i],0),-y.reduce((s,v,i)=>s+v*eye[i],0),-z.reduce((s,v,i)=>s+v*eye[i],0),1];}
function perspective(aspect){const f=1/Math.tan(Math.PI/7.2),near=.1,far=500;return[f/aspect,0,0,0,0,f,0,0,0,0,(far+near)/(near-far),-1,0,0,(2*far*near)/(near-far),0];}
function mul(a,b){const out=new Float32Array(16);for(let c=0;c<4;c++)for(let r=0;r<4;r++){
 let v=0;for(let k=0;k<4;k++)v+=a[k*4+r]*b[c*4+k];out[c*4+r]=v;}return out;}
function getChunks(bytes){const dv=new DataView(bytes);if(dv.getUint32(0,true)!==0x46546c67)throw Error('Keine GLB-Datei');let off=12,meta=null,bin=null;
 while(off+8<=bytes.byteLength){const size=dv.getUint32(off,true),kind=dv.getUint32(off+4,true);off+=8;
 if(kind===0x4e4f534a)meta=JSON.parse(new TextDecoder().decode(new Uint8Array(bytes,off,size)));
 if(kind===0x004e4942)bin=bytes.slice(off,off+size);off+=size;}
 if(!meta||!bin)throw Error('Unvollständige GLB');return{meta,bin};}
function extractMeshes(gl,bytes){const{meta,bin}=getChunks(bytes);const meshes=[];for(const node of meta.nodes||[]){if(node.mesh==null)continue;const source=meta.meshes[node.mesh];for(const primitive of source.primitives){
 const attributes={};for(const [name,index]of Object.entries(primitive.attributes)){
   const a=meta.accessors[index],v=meta.bufferViews[a.bufferView];const components={VEC2:2,VEC3:3,VEC4:4}[a.type];
   if(a.componentType!==5126||!components)throw Error('Mesh-Datenformat nicht unterstützt');
   const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
   gl.bufferData(gl.ARRAY_BUFFER,new Uint8Array(bin,v.byteOffset||0,v.byteLength),gl.STATIC_DRAW);
   attributes[name]={buffer,count:components,stride:v.byteStride||0,offset:a.byteOffset||0};
 }
 const ia=meta.accessors[primitive.indices],iv=meta.bufferViews[ia.bufferView];
 if(ia.componentType!==5123)throw Error('Index-Datenformat nicht unterstützt');
 const indices=gl.createBuffer();gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,indices);gl.bufferData(gl.ELEMENT_ARRAY_BUFFER,new Uint8Array(bin,iv.byteOffset||0,iv.byteLength),gl.STATIC_DRAW);
 meshes.push({name:node.name||source.name,attributes,indices,count:ia.count});
 }}return meshes;}
function create(canvas,url,textureCanvas){
 let gl,meshList,p,texture,disposed=false,yaw=0,pitch=0.16,distance=80,last=null,raf=0,dirtyTexture=true;
 const on={};let observe;
 const start=async()=>{
  gl=canvas.getContext('webgl',{alpha:false,antialias:true,powerPreference:'low-power',preserveDrawingBuffer:false});
  if(!gl)throw Error('WebGL wird auf diesem Gerät nicht unterstützt.');
  const response=await fetch(url);if(!response.ok)throw Error('3D-Modell konnte nicht geladen werden ('+response.status+').');
  meshList=extractMeshes(gl,await response.arrayBuffer());
  if(!meshList.some(m=>m.name==='Plane.001')||!meshList.some(m=>m.name==='Plane.002'))throw Error('Benötigte Modellteile fehlen.');
  p=program(gl);gl.useProgram(p);gl.enable(gl.DEPTH_TEST);gl.disable(gl.CULL_FACE);
  texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
  gl.uniform1i(gl.getUniformLocation(p,'uDesign'),0);
  on.down=e=>{last={x:e.clientX,y:e.clientY};canvas.setPointerCapture(e.pointerId);};
  on.move=e=>{if(!last)return;const dx=e.clientX-last.x,dy=e.clientY-last.y;last={x:e.clientX,y:e.clientY};yaw+=dx*0.008;pitch=Math.max(-.65,Math.min(.95,pitch+dy*0.007));schedule();};
  on.up=()=>{last=null;};
  on.wheel=e=>{e.preventDefault();distance=Math.max(53,Math.min(145,distance+e.deltaY*0.055));schedule();};
  canvas.addEventListener('pointerdown',on.down);canvas.addEventListener('pointermove',on.move);
  canvas.addEventListener('pointerup',on.up);canvas.addEventListener('pointercancel',on.up);
  canvas.addEventListener('wheel',on.wheel,{passive:false});
  observe=new ResizeObserver(schedule);observe.observe(canvas);
  schedule();return {refresh,dispose,reset};
 };
 function schedule(){if(disposed||!gl||!p||raf)return;raf=requestAnimationFrame(()=>{raf=0;draw();});}
 function refresh(){dirtyTexture=true;schedule();}
 function reset(){yaw=0;pitch=.16;distance=80;schedule();}
 function draw(){if(disposed||!gl||!p)return;
   const rect=canvas.getBoundingClientRect();if(!rect.width||!rect.height)return;
   const dpr=Math.min(window.devicePixelRatio||1,1.5),w=Math.min(960,Math.max(1,Math.round(rect.width*dpr))),h=Math.min(630,Math.max(1,Math.round(rect.height*dpr)));
   if(canvas.width!==w||canvas.height!==h){canvas.width=w;canvas.height=h;}
   gl.viewport(0,0,w,h);gl.clearColor(232/255,237/255,244/255,1);gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);
   gl.useProgram(p);gl.activeTexture(gl.TEXTURE0);gl.bindTexture(gl.TEXTURE_2D,texture);
   if(dirtyTexture){gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,false);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,textureCanvas);dirtyTexture=false;}
   const aim=[0,8,-27],cam=[Math.sin(yaw)*Math.cos(pitch)*distance,aim[1]+Math.sin(pitch)*distance,aim[2]-Math.cos(yaw)*Math.cos(pitch)*distance];
   gl.uniformMatrix4fv(gl.getUniformLocation(p,'uVP'),false,mul(perspective(w/h),view(cam,aim)));
   gl.uniform3fv(gl.getUniformLocation(p,'uCamera'),cam);
   for(const mesh of meshList){
     gl.uniform1i(gl.getUniformLocation(p,'uGold'),mesh.name==='Plane.002'?1:0);
     for(const [key,attr]of Object.entries({aPosition:'POSITION',aNormal:'NORMAL',aUV:'TEXCOORD_0'})){
       const loc=gl.getAttribLocation(p,key),a=mesh.attributes[attr];if(loc===-1||!a)continue;
       gl.bindBuffer(gl.ARRAY_BUFFER,a.buffer);gl.enableVertexAttribArray(loc);gl.vertexAttribPointer(loc,a.count,gl.FLOAT,false,a.stride,a.offset);
     }
     gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,mesh.indices);gl.drawElements(gl.TRIANGLES,mesh.count,gl.UNSIGNED_SHORT,0);
   }
 }
 function dispose(){disposed=true;if(raf)cancelAnimationFrame(raf);observe?.disconnect();if(!gl)return;
  canvas.removeEventListener('pointerdown',on.down);canvas.removeEventListener('pointermove',on.move);
  canvas.removeEventListener('pointerup',on.up);canvas.removeEventListener('pointercancel',on.up);canvas.removeEventListener('wheel',on.wheel);
  for(const mesh of meshList||[]){for(const item of Object.values(mesh.attributes))gl.deleteBuffer(item.buffer);gl.deleteBuffer(mesh.indices);}
  gl.deleteTexture(texture);gl.deleteProgram(p);
 }
 return start();
}
window.RM3DViewer={create};
})();
